import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { admin } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { getIntlLocale } from '../../i18n/runtime';
import {
  Loader2,
  Heart,
  MessageSquare,
  Eye,
  ExternalLink,
  Search,
  Shield,
  LogIn,
  Plus,
  MoreHorizontal,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import AdminPostComposer from './components/AdminPostComposer';

export default function AdminPosts() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [revisionDraft, setRevisionDraft] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const data = await admin.getUsers({ limit: 100 });
      setUsers(data.users || []);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadPosts = useCallback(async (userId) => {
    try {
      setPostsLoading(true);
      const data = await admin.getUserPosts(userId);
      setPosts((data.posts || []).map((p) => {
        if (p.source === 'approval' && (p.has_image || p.has_video || p.has_document)) {
          return { ...p, media_url: admin.postApprovalMediaUrl(userId, p.id) };
        }
        return p;
      }));
      setHasAccount(!!data.has_account);
    } catch (err) {
      setPosts([]);
      setHasAccount(false);
      // eslint-disable-next-line no-alert
      alert(err.message || 'Failed to load posts');
    } finally {
      setPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (selected) loadPosts(selected.id);
  }, [selected, loadPosts]);

  const filteredUsers = users.filter((u) =>
    !search ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleEnterAs = async (target) => {
    if (impersonatingId) return;
    setImpersonatingId(target.id);
    try {
      await admin.impersonate(target.id);
      await checkAuth();
      navigate('/app/posts');
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message || 'Failed to enter user');
    } finally {
      setImpersonatingId(null);
    }
  };

  return (
    <div className="app-page-shell bg-white">
      <header className="h-14 border-b border-[#E3E2E0] flex items-center px-6 sticky top-0 bg-white z-10">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#6B6B6B]" />
          <h1 className="text-lg font-semibold text-[#191919]">Admin Dashboard</h1>
        </div>
        <nav className="ml-8 flex items-center gap-1 text-sm">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              `px-3 py-1.5 rounded-lg ${isActive ? 'bg-[#F0F0EF] text-[#191919] font-medium' : 'text-[#6B6B6B] hover:bg-[#F7F7F5]'}`
            }
            data-testid="admin-tab-users"
          >
            Users
          </NavLink>
          <NavLink
            to="/admin/posts"
            className={({ isActive }) =>
              `px-3 py-1.5 rounded-lg ${isActive ? 'bg-[#F0F0EF] text-[#191919] font-medium' : 'text-[#6B6B6B] hover:bg-[#F7F7F5]'}`
            }
            data-testid="admin-tab-posts"
          >
            Posts
          </NavLink>
        </nav>
      </header>

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left: user list */}
        <aside className="w-72 border-r border-[#E3E2E0] flex flex-col bg-[#FBFBFA]">
          <div className="p-3 border-b border-[#E3E2E0]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B9B9B]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-9 pr-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]"
                data-testid="admin-posts-user-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-[#6B6B6B]" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <p className="text-center text-xs text-[#6B6B6B] py-8">No users</p>
            ) : (
              filteredUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setSelected(u)}
                  className={`w-full text-left p-3 border-b border-[#E3E2E0] hover:bg-white transition-colors ${
                    selected?.id === u.id ? 'bg-white' : ''
                  }`}
                  data-testid={`admin-posts-user-${u.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#191919] truncate">{u.name || u.email}</p>
                      <p className="text-xs text-[#6B6B6B] truncate">{u.email}</p>
                    </div>
                    {u.linkedin_connected ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E6F4EA] text-[#1E7F4E] shrink-0">LI</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F0F0EF] text-[#6B6B6B] shrink-0">—</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Right: posts for selected user */}
        <section className="flex-1 overflow-y-auto">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-center px-6">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-[#FBFBFA] border border-[#E3E2E0] flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-[#6B6B6B]" />
                </div>
                <p className="text-sm text-[#6B6B6B]">Pick a user on the left to view their posts.</p>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto p-6 space-y-4">
              <header className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-[#191919]">
                    {selected.name || selected.email}
                  </h2>
                  <p className="text-xs text-[#6B6B6B]">{selected.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setRevisionDraft(null); setComposerOpen(true); }}
                    disabled={!hasAccount}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#0A0A0A] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    title={hasAccount ? 'Create a post on this user\u2019s LinkedIn' : 'User has no connected LinkedIn account'}
                    data-testid="admin-create-post-btn"
                  >
                    <Plus className="w-4 h-4" /> New post on behalf
                  </button>
                  <button
                    onClick={() => handleEnterAs(selected)}
                    disabled={impersonatingId === selected.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-[#E3E2E0] rounded-lg hover:bg-[#F7F7F5] disabled:opacity-60"
                    data-testid={`admin-posts-impersonate-${selected.id}`}
                  >
                    {impersonatingId === selected.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                    Enter as user
                  </button>
                </div>
              </header>

              {!hasAccount && (
                <div className="rounded-lg border border-[#FFD9A8] bg-[#FFF4E5] p-3 text-xs text-[#7A3E00] flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <p>This user has not connected a LinkedIn account yet, so no live posts can be loaded and posting on their behalf is disabled.</p>
                </div>
              )}

              {postsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#6B6B6B]" />
                </div>
              ) : posts.length === 0 ? (
                <p className="text-center text-sm text-[#6B6B6B] py-8">No posts yet.</p>
              ) : (
                posts.map((p) => (
                  <AdminPostCard
                    key={`${p.source}-${p.id}`}
                    p={p}
                    onRevise={() => { setRevisionDraft(p); setComposerOpen(true); }}
                  />
                ))
              )}
            </div>
          )}
        </section>
      </div>

      {composerOpen && selected && (
        <AdminPostComposer
          target={selected}
          onClose={() => { setComposerOpen(false); setRevisionDraft(null); }}
          onSent={() => {
            setComposerOpen(false);
            setRevisionDraft(null);
            loadPosts(selected.id);
          }}
          initialDraft={revisionDraft}
        />
      )}
    </div>
  );
}

function AdminPostCard({ p, onRevise }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const formatNum = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0));
  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString(getIntlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };
  const m = p.metrics_available || {};
  const isApproval = p.source === 'approval';
  const statusLabel = {
    pending: 'Pending client review',
    rejected: 'Changes requested',
    approved: 'Approved / scheduled',
    failed: 'Publish failed',
  }[p.status] || p.status;

  return (
    <article className="border border-[#E3E2E0] rounded-xl bg-white p-4" data-testid={`admin-post-${p.id}`}>
      <header className="flex items-center justify-between mb-2 text-xs text-[#6B6B6B]">
        <div className="flex items-center gap-2 flex-wrap">
          <span>{fmtDate(p.created_at)}</span>
          {isApproval && (
            <span className={`inline-flex items-center rounded-md px-2 py-0.5 border ${
              p.status === 'rejected'
                ? 'bg-[#FFEBE9] text-[#CE3636] border-[#FF8080]'
                : p.status === 'pending'
                  ? 'bg-[#FFF4E5] text-[#7A3E00] border-[#FFD9A8]'
                  : 'bg-[#F0F0EF] text-[#6B6B6B] border-[#E3E2E0]'
            }`}>
              {statusLabel}
            </span>
          )}
          {isApproval && p.revision_count > 0 && (
            <span className="text-[#9B9B9B]">revision {p.revision_count}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isApproval && p.can_revise && (
            <button
              onClick={onRevise}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#0A0A0A] text-white text-xs font-medium hover:bg-black"
              data-testid={`admin-revise-approval-${p.id}`}
            >
              <Plus className="w-3 h-3" />
              Edit & resend
            </button>
          )}
          {p.url && (
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#0052CC] hover:underline"
              data-testid={`admin-post-open-${p.id}`}
            >
              <ExternalLink className="w-3 h-3" /> Open on LinkedIn
            </a>
          )}
          <div className="relative">
            <button onClick={() => setMenuOpen((o) => !o)} className="p-1 hover:bg-[#F0F0EF] rounded" aria-haspopup="menu">
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 w-56 rounded-lg border border-[#E3E2E0] bg-white shadow-lg z-20 py-1">
                <button
                  disabled
                  title="Not supported by Unipile — open the post on LinkedIn to delete it manually."
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#9B9B9B] cursor-not-allowed text-left"
                  data-testid={`admin-post-delete-${p.id}`}
                >
                  <Trash2 className="w-4 h-4" /> Delete (provider unsupported)
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {isApproval && p.rejection_comment && (
        <div className="mb-3 rounded-lg border border-[#FF8080] bg-[#FFEBE9] px-3 py-2">
          <p className="text-[11px] font-semibold text-[#CE3636] mb-1">Client requested changes</p>
          <p className="text-sm text-[#7A1F1F] whitespace-pre-wrap">{p.rejection_comment}</p>
        </div>
      )}
      {isApproval && p.publish_error && (
        <div className="mb-3 rounded-lg border border-[#FFD9A8] bg-[#FFF4E5] px-3 py-2">
          <p className="text-[11px] font-semibold text-[#7A3E00] mb-1">Publishing error</p>
          <p className="text-sm text-[#7A3E00] whitespace-pre-wrap">{p.publish_error}</p>
        </div>
      )}
      <p className="text-sm text-[#191919] whitespace-pre-wrap leading-relaxed">{p.text}</p>
      {(p.image_url || (isApproval && p.has_image && p.media_url)) && (
        <img src={p.image_url || p.media_url} alt="" className="mt-3 rounded-lg border border-[#E3E2E0] max-h-64 object-cover" />
      )}
      {isApproval && p.has_video && p.media_url && (
        <video src={p.media_url} controls className="mt-3 rounded-lg border border-[#E3E2E0] max-h-64 bg-black w-full" />
      )}
      {isApproval && p.has_document && p.media_url && (
        <a href={p.media_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#E3E2E0] px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5]">
          <FileText className="w-4 h-4 text-[#6B6B6B]" />
          {p.document_name || 'Open document'}
        </a>
      )}
      <div className="mt-3 flex items-center gap-5 text-xs text-[#6B6B6B]">
        <Stat icon={Heart} label="likes" value={p.likes} available={m.likes} formatNum={formatNum} />
        <Stat icon={MessageSquare} label="comments" value={p.comments} available={m.comments} formatNum={formatNum} />
        <Stat icon={Eye} label="views" value={p.views} available={m.views} formatNum={formatNum} />
      </div>
    </article>
  );
}

function Stat({ icon: Icon, label, value, available, formatNum }) {
  const isMissing = available === false;
  return (
    <span
      className="inline-flex items-center gap-1"
      title={isMissing ? `${label} not provided by LinkedIn for this account` : undefined}
    >
      <Icon className="w-3.5 h-3.5" /> {isMissing ? '—' : formatNum(value || 0)} {label}
    </span>
  );
}

